/**
 * The intermediate flow-graph model.
 *
 * Deliberately independent of any rendering or layout library: the compiler
 * emits this, the layout engine adds geometry to it, the router adds polyline
 * points to its edges, and the SVG renderer reads it. Swapping any one of those
 * three out does not touch the other two.
 */
import type { SourceRange } from '../../language/ast/source';
import type { NodeMetadata } from '../../language/ast/nodes';

/* ------------------------------------------------------------------ *
 * Geometry primitives
 * ------------------------------------------------------------------ */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Rect extends Point, Size {}

/** Which side of a node an edge attaches to. */
export type PortSide = 'top' | 'right' | 'bottom' | 'left';

/* ------------------------------------------------------------------ *
 * Nodes
 * ------------------------------------------------------------------ */

/**
 * Semantic role — what the node *means*. Drives edge semantics and the
 * accessibility outline.
 */
export type NodeRole =
  | 'start'
  | 'end'
  | 'action'
  | 'decision'
  | 'loop'
  | 'call'
  | 'io'
  | 'junction'
  | 'note'
  | 'return'
  | 'function-entry'
  | 'function-exit'
  | 'error';

/**
 * Visual shape — how the node is *drawn*. Usually derived from the role, but
 * `@shape(...)` can override it.
 */
export type NodeShape =
  'terminator' | 'process' | 'decision' | 'subprocess' | 'io' | 'junction' | 'note' | 'error';

export interface GraphNode {
  /** Stable across edits that don't change the node's structural position. */
  readonly id: string;
  readonly role: NodeRole;
  readonly shape: NodeShape;
  /** Display text, verbatim from the source. Never HTML. */
  readonly label: string;
  /** Text pre-wrapped into display lines by the measurement pass. */
  readonly lines: readonly string[];
  /** Source span to reveal when the node is selected. */
  readonly range: SourceRange | null;
  /** Incoming edge ids. Filled by `indexGraph`. */
  readonly incoming: readonly string[];
  /** Outgoing edge ids. Filled by `indexGraph`. */
  readonly outgoing: readonly string[];
  readonly metadata: NodeMetadata;
  /** Measured/estimated box size. Set by the measurement pass. */
  readonly size: Size;
  /** Top-left position in graph space. Set by the layout pass. */
  readonly position: Point;
  /**
   * Id of the function-expansion group this node belongs to, or `null` for the
   * main flow. Used to draw the dashed function frame in expanded mode.
   */
  readonly groupId: string | null;
  /** Nesting depth, used for subtle indentation cues and for the a11y outline. */
  readonly depth: number;
  /** For `call` nodes: the normalized name of the callee. */
  readonly calleeName?: string;
  /** For nodes belonging to a function body: the function's normalized name. */
  readonly functionName?: string;
}

/* ------------------------------------------------------------------ *
 * Edges
 * ------------------------------------------------------------------ */

export type EdgeType =
  | 'flow'
  | 'true'
  | 'false'
  | 'case'
  | 'default'
  | 'loop-back'
  | 'loop-exit'
  | 'call'
  | 'return'
  | 'break'
  | 'continue';

export interface GraphEdge {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly sourcePort: PortSide;
  readonly targetPort: PortSide;
  /** Edge label, e.g. `Yes` / `No` / a switch case value. Empty means unlabelled. */
  readonly label: string;
  readonly type: EdgeType;
  /**
   * Orthogonal polyline in graph space, start to end. Guaranteed by the router
   * that every consecutive pair shares an x or a y. Empty until routed.
   */
  readonly points: readonly Point[];
  /** Where the label is drawn. Set by the router. */
  readonly labelPosition: Point | null;
  readonly range: SourceRange | null;
  /** True when the edge closes a loop — rendered with a distinct arrow style. */
  readonly isBackEdge: boolean;
}

/* ------------------------------------------------------------------ *
 * Groups (function expansion frames)
 * ------------------------------------------------------------------ */

export interface GraphGroup {
  readonly id: string;
  readonly label: string;
  /** Normalized function name. */
  readonly functionName: string;
  readonly nodeIds: readonly string[];
  /** Bounding box, computed after layout. */
  readonly bounds: Rect | null;
  readonly range: SourceRange | null;
  /** Nesting depth of the expansion — recursion guard uses this. */
  readonly depth: number;
}

/* ------------------------------------------------------------------ *
 * Graph
 * ------------------------------------------------------------------ */

export type FlowDirection = 'top-to-bottom' | 'left-to-right';

export interface GraphBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly width: number;
  readonly height: number;
}

export interface FlowGraph {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
  readonly groups: readonly GraphGroup[];
  readonly direction: FlowDirection;
  /** Overall extents after layout; `null` before layout runs. */
  readonly bounds: GraphBounds | null;
  /**
   * Set when a safety limit stopped compilation early. The partial graph is
   * still renderable; the UI surfaces this as a warning banner.
   */
  readonly truncated: boolean;
}

export const EMPTY_GRAPH: FlowGraph = Object.freeze({
  nodes: [],
  edges: [],
  groups: [],
  direction: 'top-to-bottom' as FlowDirection,
  bounds: null,
  truncated: false,
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

export function nodeCenter(node: GraphNode): Point {
  return {
    x: node.position.x + node.size.width / 2,
    y: node.position.y + node.size.height / 2,
  };
}

export function nodeRect(node: GraphNode): Rect {
  return {
    x: node.position.x,
    y: node.position.y,
    width: node.size.width,
    height: node.size.height,
  };
}

/** Anchor point on a given side of a node. */
export function portPoint(node: GraphNode, side: PortSide): Point {
  const { x, y } = node.position;
  const { width, height } = node.size;
  switch (side) {
    case 'top':
      return { x: x + width / 2, y };
    case 'bottom':
      return { x: x + width / 2, y: y + height };
    case 'left':
      return { x, y: y + height / 2 };
    case 'right':
      return { x: x + width, y: y + height / 2 };
  }
}

export function oppositeSide(side: PortSide): PortSide {
  switch (side) {
    case 'top':
      return 'bottom';
    case 'bottom':
      return 'top';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
  }
}

/** Rebuilds every node's `incoming`/`outgoing` from the edge list. */
export function indexGraph(graph: FlowGraph): FlowGraph {
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  const known = new Set(graph.nodes.map((n) => n.id));

  for (const edge of graph.edges) {
    if (!known.has(edge.source) || !known.has(edge.target)) continue;
    (outgoing.get(edge.source) ?? outgoing.set(edge.source, []).get(edge.source)!).push(edge.id);
    (incoming.get(edge.target) ?? incoming.set(edge.target, []).get(edge.target)!).push(edge.id);
  }

  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      incoming: incoming.get(node.id) ?? [],
      outgoing: outgoing.get(node.id) ?? [],
    })),
  };
}

/** Bounding box over all node rectangles, `null` for an empty graph. */
export function computeBounds(nodes: readonly GraphNode[]): GraphBounds | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + node.size.width);
    maxY = Math.max(maxY, node.position.y + node.size.height);
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/** Bounding box over nodes *and* every routed edge point. */
export function computeFullBounds(graph: FlowGraph): GraphBounds | null {
  const base = computeBounds(graph.nodes);
  if (!base) return null;
  let { minX, minY, maxX, maxY } = base;
  for (const edge of graph.edges) {
    for (const p of edge.points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function findNode(graph: FlowGraph, id: string): GraphNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

export function findEdge(graph: FlowGraph, id: string): GraphEdge | undefined {
  return graph.edges.find((e) => e.id === id);
}

/** Default shape for a role, before any `@shape(...)` override. */
export function shapeForRole(role: NodeRole): NodeShape {
  switch (role) {
    case 'start':
    case 'end':
    case 'function-entry':
    case 'function-exit':
      return 'terminator';
    case 'decision':
    case 'loop':
      return 'decision';
    case 'call':
      return 'subprocess';
    case 'io':
      return 'io';
    case 'junction':
      return 'junction';
    case 'note':
      return 'note';
    case 'error':
      return 'error';
    case 'return':
    case 'action':
      return 'process';
  }
}
